import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { startRegistration, startAuthentication } from '@simplewebauthn/browser';
import { toast } from 'sonner';

export interface Passkey {
  id: string;
  name: string;
  device_type: string | null;
  backed_up: boolean;
  last_used_at: string | null;
  created_at: string;
}

export function usePasskeys() {
  const [keys, setKeys] = useState<Passkey[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('webauthn_credentials')
      .select('id, name, device_type, backed_up, last_used_at, created_at')
      .order('created_at', { ascending: false });
    if (error) toast.error(`Błąd pobierania passkeys: ${error.message}`);
    else setKeys(data ?? []);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetch();
  }, [fetch]);

  const register = useCallback(
    async (name: string) => {
      setBusy(true);
      try {
        const { data: opts, error: oErr } = await supabase.functions.invoke(
          'webauthn-register-options'
        );
        if (oErr || !opts) throw new Error(oErr?.message || 'Brak opcji rejestracji');

        const attResp = await startRegistration({ optionsJSON: opts });

        const { data: vRes, error: vErr } = await supabase.functions.invoke(
          'webauthn-register-verify',
          { body: { response: attResp, name } }
        );
        if (vErr || !vRes?.success) {
          throw new Error(vErr?.message || vRes?.error || 'Weryfikacja nieudana');
        }

        toast.success('Passkey dodany');
        await fetch();
        return true;
      } catch (e: any) {
        if (e?.name === 'InvalidStateError') {
          toast.error('Ten klucz jest już zarejestrowany');
        } else if (e?.name === 'NotAllowedError') {
          toast.error('Anulowano przez użytkownika');
        } else {
          toast.error(`Błąd: ${e?.message || e}`);
        }
        return false;
      } finally {
        setBusy(false);
      }
    },
    [fetch]
  );

  const remove = useCallback(
    async (id: string) => {
      const { error } = await supabase.from('webauthn_credentials').delete().eq('id', id);
      if (error) {
        toast.error(`Nie udało się usunąć: ${error.message}`);
        return;
      }
      toast.success('Passkey usunięty');
      await fetch();
    },
    [fetch]
  );

  const rename = useCallback(
    async (id: string, name: string) => {
      const { error } = await supabase.from('webauthn_credentials').update({ name }).eq('id', id);
      if (error) toast.error(`Błąd: ${error.message}`);
      else await fetch();
    },
    [fetch]
  );

  return { keys, loading, busy, register, remove, rename, refresh: fetch };
}

/**
 * Sign in with a passkey (resident key / no-login). Returns true on success.
 */
export async function signInWithPasskey(): Promise<boolean> {
  try {
    const { data: opts, error: oErr } = await supabase.functions.invoke('webauthn-auth-options');
    if (oErr || !opts) throw new Error(oErr?.message || 'Brak opcji logowania');

    const assertion = await startAuthentication({ optionsJSON: opts });

    const { data: vRes, error: vErr } = await supabase.functions.invoke('webauthn-auth-verify', {
      body: { response: assertion },
    });
    if (vErr || !vRes?.success || !vRes?.token_hash || !vRes?.email) {
      throw new Error(vErr?.message || vRes?.error || 'Weryfikacja nieudana');
    }

    const { error: otpErr } = await supabase.auth.verifyOtp({
      type: 'magiclink',
      token_hash: vRes.token_hash,
    });
    if (otpErr) throw otpErr;

    return true;
  } catch (e: any) {
    if (e?.name === 'NotAllowedError') {
      toast.error("Anulowano logowanie passkey'em");
    } else {
      toast.error(`Logowanie passkey'em nieudane: ${e?.message || e}`);
    }
    return false;
  }
}
